import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import { FleetMap } from "@/components/routing/FleetMap";
import { HealthPill, DutyPill, HEALTH, ago } from "@/components/routing/TrackingPills";
import { Truck, Route as RouteIcon, Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DriverRow = ({ d, active, onSelect }) => (
  <button onClick={() => onSelect(d.driver_id)} data-testid="routes-driver-row"
    className={`w-full text-left p-3 border-b border-border last:border-0 transition-colors ${active ? "bg-primary/5" : "hover:bg-muted/50"}`}>
    <div className="flex items-center justify-between gap-2"><span className="font-semibold text-sm">{d.name}</span><DutyPill value={d.duty_status} /></div>
    <div className="flex items-center justify-between gap-2 mt-1.5">
      <HealthPill value={d.tracking_health} />
      <span className="text-[11px] text-muted-foreground">{d.last_location ? `updated ${ago(d.seconds_since_update)}` : "no location"}</span>
    </div>
    <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Truck className="w-3 h-3" />{d.vehicle ? d.vehicle.code : "No vehicle"} · {d.trips_today} trip(s) today</p>
  </button>
);

const DriverDetail = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/dispatch/routes/drivers/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-primary m-4" />;
  return (
    <div className="p-4" data-testid="routes-driver-detail">
      <div className="flex items-center justify-between mb-2"><h3 className="font-heading font-semibold">{data.driver.name}</h3><button onClick={onClose} aria-label="Close" data-testid="routes-detail-close"><X className="w-4 h-4" /></button></div>
      <p className="text-xs text-muted-foreground mb-3">Today ({data.date}) · {data.route.length} location point(s) · {data.sessions.length} shift(s)</p>
      <div className="space-y-2">
        {data.trips.length === 0 && <p className="text-xs text-muted-foreground">No trips today.</p>}
        {data.trips.map((t) => (
          <div key={t.id} className="border border-border rounded-md p-2 text-xs" data-testid="routes-trip">
            <div className="flex justify-between gap-2"><b>{(t.scheduled_at || "").slice(11, 16)} {t.client_name || t.title}</b><StatusBadge value={t.status} /></div>
            <p className="text-muted-foreground mt-0.5">{t.pickup || "—"} → {t.dropoff || "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function DispatchRoutes() {
  const [ov, setOv] = useState(null);
  const [feed, setFeed] = useState([]);
  const [sel, setSel] = useState(null);
  const [route, setRoute] = useState([]);
  const load = useCallback(() => {
    api.get("/dispatch/routes/overview").then((r) => setOv(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/dispatch/routes/feed").then((r) => setFeed(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (!sel) { setRoute([]); return; } api.get(`/dispatch/routes/drivers/${sel}`).then((r) => setRoute(r.data.route)).catch(() => setRoute([])); }, [sel]);

  const drivers = ov?.drivers || [];
  const onShift = drivers.filter((d) => d.duty_status !== "OFF_DUTY");
  const markers = [
    ...onShift.filter((d) => d.last_location).map((d) => ({ id: d.driver_id, lat: d.last_location.lat, lng: d.last_location.lng, label: d.name?.[0], title: `${d.name} · ${HEALTH[d.tracking_health]?.[0]}`, color: HEALTH[d.tracking_health]?.[2] })),
    ...route.slice(-200).filter((_, i, a) => i % Math.max(1, Math.floor(a.length / 40)) === 0).map((p, i) => ({ id: `r${i}`, lat: p.lat, lng: p.lng, label: "·", title: fmtDate(p.received_at), color: "#C8202F" })),
  ];
  const counts = Object.keys(HEALTH).filter((k) => k !== "OFF_DUTY").map((k) => [k, onShift.filter((d) => d.tracking_health === k).length]).filter(([, n]) => n);

  return (
    <div data-testid="dispatch-routes-page">
      <PageHeader title="Dispatch & Routes" subtitle="On-shift drivers, tracking health, vehicles and today's trips. Refreshes every 10 seconds." />
      <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="routes-health-summary">
        <span className="font-semibold">{onShift.length} on shift</span>
        {counts.map(([k, n]) => <span key={k} className="flex items-center gap-1"><HealthPill value={k} /> {n}</span>)}
        {ov && <span className="text-muted-foreground">Stale after {ov.policy.stale_after_s}s · offline after {ov.policy.offline_after_s}s</span>}
      </div>
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="border border-border rounded-lg bg-card overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="routes-driver-list">
          {!ov && <Loader2 className="w-5 h-5 animate-spin text-primary m-4" />}
          {ov && drivers.length === 0 && <p className="p-4 text-sm text-muted-foreground">No drivers.</p>}
          {drivers.map((d) => <DriverRow key={d.driver_id} d={d} active={sel === d.driver_id} onSelect={setSel} />)}
        </div>
        <div className="space-y-4 min-w-0">
          <FleetMap markers={markers} height={420} testid="routes-map" />
          {sel && <div className="border border-border rounded-lg bg-card"><DriverDetail id={sel} onClose={() => setSel(null)} /></div>}
          <div className="border border-border rounded-lg bg-card" data-testid="routes-status-feed">
            <div className="flex items-center gap-2 p-3 border-b border-border"><Bell className="w-4 h-4 text-brand" /><h3 className="font-heading font-semibold text-sm">Driver status updates</h3></div>
            {feed.length === 0 && <p className="p-3 text-xs text-muted-foreground">No status updates yet.</p>}
            {feed.map((n) => <div key={n.id} className="px-3 py-2 text-xs border-b border-border last:border-0 flex justify-between gap-2" data-testid="routes-feed-item"><span><b>{n.title}</b> — {n.message}</span><span className="text-muted-foreground whitespace-nowrap">{fmtDate(n.created_at)}</span></div>)}
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><RouteIcon className="w-3 h-3" /> Only today's route is shown. Each view of a driver's route is recorded in the audit log.</p>
        </div>
      </div>
    </div>
  );
}
